import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { AccountSetupPreview, User } from 'shared'
import { Alert, Button, Field, FullPageSpinner, Input } from '../components/ui'
import { homePathFor, useAuth } from '../contexts/AuthContext'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import Logo from '../components/ui/Logo'

/** Mirrors the backend's shared rule; the server checks it again regardless. */
const MIN_PASSWORD_LENGTH = 8

/**
 * Dove una persona creata da un admin sceglie la propria password.
 *
 * Pubblica, perché chi apre il link non ha ancora un account con cui entrare.
 * Un link sbagliato, scaduto o già usato dicono tutti la stessa cosa: da
 * fuori non si distinguono, ed è voluto.
 */
export default function AccountSetup() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const { setSession } = useAuth()

  const [preview, setPreview] = useState<AccountSetupPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ data: AccountSetupPreview }>(`/api/account-setup/${token}`)
      .then((response) => setPreview(response.data))
      .catch(() => setPreview(null))
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const response = await api.post<{ token: string; user: User }>(
        `/api/account-setup/${token}`,
        { password }
      )

      // The session starts here: someone who has just chosen a password should
      // not have to type it again into the sign-in form.
      setSession(response.token, response.user)
      navigate(homePathFor(response.user), { replace: true })
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
      setSubmitting(false)
    }
  }

  if (loading) return <FullPageSpinner />

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-950">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Logo />
        </div>

        {!preview ? (
          <div className="rounded-lg bg-white p-8 text-center shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('accountSetup.invalidTitle')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('accountSetup.invalidDescription')}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              {t('auth.login')}
            </Link>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('accountSetup.title')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('accountSetup.description', { name: preview.name, email: preview.email })}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Field
                label={t('auth.password')}
                required
                hint={t('auth.passwordHint', { min: MIN_PASSWORD_LENGTH })}
              >
                <Input
                  type="password"
                  name="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              {error && <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>}

              <Button type="submit" className="w-full" loading={submitting}>
                {t('accountSetup.submit')}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
